import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DefaultButton from "../../../components/buttons/defaultButton";
import DefaultBadge from "../../../components/badge/defaultBadge";
import { HiDownload, HiRefresh, HiEye } from "react-icons/hi";
import { CgSpinner } from "react-icons/cg";

const ImportJobs = () => {
  const navigate = useNavigate();
  const [importJobs, setImportJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading import jobs
    setTimeout(() => {
      const mockJobs = [
        {
          id: "import-1234567890",
          filename: "employees_jan_2024.csv",
          status: "completed",
          total: 100,
          imported: 85,
          failed: 10,
          skipped: 5,
          startedAt: new Date(Date.now() - 3600000),
          completedAt: new Date(Date.now() - 3000000),
        },
        {
          id: "import-1234567891",
          filename: "employees_feb_2024.csv",
          status: "processing",
          total: 250,
          imported: 150,
          failed: 5,
          skipped: 0,
          startedAt: new Date(Date.now() - 1800000),
          completedAt: null,
        },
        {
          id: "import-1234567892",
          filename: "employees_mar_2024.csv",
          status: "failed",
          total: 50,
          imported: 0,
          failed: 50,
          skipped: 0,
          startedAt: new Date(Date.now() - 7200000),
          completedAt: new Date(Date.now() - 7000000),
        },
      ];
      setImportJobs(mockJobs);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <DefaultBadge status={1} custom="Completed" />;
      case "processing":
        return <DefaultBadge status={0} custom="Processing" />;
      case "failed":
        return <DefaultBadge status={2} custom="Failed" />;
      default:
        return <DefaultBadge status={0} custom="Pending" />;
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString();
  };

  const handleViewDetails = (jobId) => {
    navigate(`/hr/import-jobs/${jobId}`);
  };

  const handleDownloadFailed = (job) => {
    // Simulate download
  };

  const handleDownloadReport = (job) => {
    // Simulate download
  };

  return (
    <div className="flex flex-col gap-6 p-6 bg-primary-50 min-h-screen">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-theme">Import Jobs</h1>
          <p className="text-sm text-textLight mt-1">
            View and manage your bulk import jobs
          </p>
        </div>
        <DefaultButton
          variant="default"
          name="New Import"
          onClick={() => navigate("/hr/onboarding")}
        />
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <CgSpinner className="animate-spin text-theme text-3xl" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-borderColor overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" role="table" aria-label="Import jobs table">
              <thead className="bg-primary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-theme uppercase">
                    Job ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-theme uppercase">
                    Filename
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-theme uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-theme uppercase">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-theme uppercase">
                    Started
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-theme uppercase">
                    Completed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-theme uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borderColor">
                {importJobs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-textLight">
                      No import jobs found
                    </td>
                  </tr>
                ) : (
                  importJobs.map((job) => {
                    const progress = job.total > 0
                      ? Math.round(((job.imported + job.failed + job.skipped) / job.total) * 100)
                      : 0;

                    return (
                      <tr key={job.id} className="hover:bg-primary-50">
                        <td className="px-6 py-4 text-sm text-theme">
                          {job.id.slice(0, 12)}...
                        </td>
                        <td className="px-6 py-4 text-sm text-theme">
                          {job.filename}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {job.status === "processing" && (
                              <CgSpinner className="animate-spin text-theme" />
                            )}
                            {getStatusBadge(job.status)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-primary-100 rounded-full h-2 max-w-32">
                              <div
                                className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] h-2 rounded-full transition-all"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                            <span className="text-xs text-textLight">{progress}%</span>
                          </div>
                          <div className="text-xs text-textLight mt-1">
                            {job.imported} imported, {job.failed} failed, {job.skipped} skipped
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-textLight">
                          {formatDate(job.startedAt)}
                        </td>
                        <td className="px-6 py-4 text-sm text-textLight">
                          {formatDate(job.completedAt)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <DefaultButton
                              variant="icon"
                              icon={<HiEye />}
                              size="icon"
                              onClick={() => handleViewDetails(job.id)}
                              tooltip="View Details"
                            />
                            {job.status === "completed" && job.failed > 0 && (
                              <DefaultButton
                                variant="icon"
                                icon={<HiDownload />}
                                size="icon"
                                onClick={() => handleDownloadFailed(job)}
                                tooltip="Download Failed Rows"
                              />
                            )}
                            {job.status === "completed" && (
                              <DefaultButton
                                variant="icon"
                                icon={<HiDownload />}
                                size="icon"
                                onClick={() => handleDownloadReport(job)}
                                tooltip="Download Report"
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportJobs;

